import { SPECIALTIES, specialtyLabel, type ClinicianSummary, type Specialty } from "@so/shared";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useClinicians } from "../api/queries.js";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Loading,
  PageHeader,
  Select,
  cx,
} from "../components/ui.js";

const LANGUAGE_NAMES = new Intl.DisplayNames(["en"], { type: "language" });

function languageList(codes: readonly string[]): string {
  return codes.map((code) => LANGUAGE_NAMES.of(code) ?? code).join(", ");
}

export function SpecialistCard({
  clinician,
  className,
}: {
  readonly clinician: ClinicianSummary;
  readonly className?: string;
}) {
  return (
    <Card as="li" className={cx("hover:border-line-strong transition-colors", className)}>
      <Link to={`/specialists/${clinician.clinician_id}`} className="block px-5 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[16.5px] font-semibold">{clinician.name}</p>
            <p className="text-ink-soft mt-0.5 text-[14px]">{clinician.credentials}</p>
          </div>
          <Badge tone="accent">{specialtyLabel(clinician.specialty)}</Badge>
        </div>

        <p className="text-ink-soft mt-3 text-[14.5px] leading-relaxed">{clinician.bio}</p>

        <dl className="text-ink-faint mt-4 flex flex-wrap gap-x-6 gap-y-1.5 text-[13px]">
          <div className="flex gap-1.5">
            <dt className="sr-only">Hospital</dt>
            <dd>{clinician.hospital}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="sr-only">Experience</dt>
            <dd>{clinician.years_experience} years</dd>
          </div>
          {/* Multilingual care is a large part of what this service sells, so
              it belongs on the card rather than two clicks deeper. */}
          <div className="flex gap-1.5">
            <dt className="sr-only">Languages</dt>
            <dd>Speaks {languageList(clinician.languages)}</dd>
          </div>
        </dl>
      </Link>
    </Card>
  );
}

export function Specialists() {
  const [specialty, setSpecialty] = useState<Specialty | "">("");
  const { data: clinicians, isPending, error } = useClinicians(specialty === "" ? undefined : specialty);

  return (
    <>
      <PageHeader eyebrow="Find a specialist" title="Specialists in our network">
        Every specialist here practises at an accredited international hospital and reviews
        second-opinion cases regularly. Choose a field to narrow the list.
      </PageHeader>

      <div className="mb-6 max-w-xs">
        <Select
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value as Specialty | "")}
          aria-label="Filter by specialty"
        >
          <option value="">All specialties</option>
          {SPECIALTIES.map((value) => (
            <option key={value} value={value}>
              {specialtyLabel(value)}
            </option>
          ))}
        </Select>
      </div>

      {isPending ? (
        <Loading label="Loading specialists" />
      ) : error ? (
        <ErrorNote error={error} />
      ) : clinicians.length === 0 ? (
        <EmptyState
          title="No specialists in that field yet"
          action={<Button variant="secondary" onClick={() => setSpecialty("")}>Show all specialties</Button>}
        >
          Our network is growing. Contact the coordination team and we will find someone suitable.
        </EmptyState>
      ) : (
        <ul className="space-y-3">
          {clinicians.map((clinician) => (
            <SpecialistCard key={clinician.clinician_id} clinician={clinician} />
          ))}
        </ul>
      )}
    </>
  );
}
